// 데이터베이스별 캐싱을 위한 Map
const dbPromises = new Map<string, Promise<IDBDatabase>>();

// 데이터베이스 열기 함수
export const openDB = (
  dbName: string,
  version: number,
  onUpgradeNeeded: (event: IDBVersionChangeEvent) => void,
): Promise<IDBDatabase> => {
  if (dbPromises.has(dbName)) return dbPromises.get(dbName)!;

  const dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, version);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      console.info(
        `IndexedDB [${dbName}] upgrading to ${version} from ${event.oldVersion}`,
      );
      onUpgradeNeeded(event);
    };
  });

  dbPromises.set(dbName, dbPromise);
  return dbPromise;
};

// 데이터 타입 정의
export interface StoredData<T> {
  id: string | number;
  data: T;
}

// 데이터 저장
export const setData = async <T>(
  db: IDBDatabase,
  table: string,
  id: string | number,
  data: T,
  validTables: string[],
): Promise<void> => {
  if (!validTables.includes(table)) {
    throw new Error(
      `Object store '${table}' does not exist in database '${db.name}'.`,
    );
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([table], "readwrite");
    const store = transaction.objectStore(table);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    store.put({ id, data });
  });
};

// 데이터 조회
export const getData = async <T>(
  db: IDBDatabase,
  table: string,
  id: string | number,
  validTables: string[],
): Promise<StoredData<T> | null> => {
  if (!validTables.includes(table)) return null;

  return new Promise((resolve) => {
    const transaction = db.transaction([table], "readonly");
    const store = transaction.objectStore(table);
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result;
      if (!result) {
        resolve(null);
        return;
      }

      let parsedData: T;
      if (typeof result.data === "string") {
        try {
          parsedData = JSON.parse(result.data) as T;
        } catch (e) {
          console.warn(
            `Failed to parse data for ${id} in ${table} (DB: ${db.name}):`,
            e,
          );
          parsedData = result.data as T;
        }
      } else {
        parsedData = result.data as T;
      }

      resolve({
        id: result.id,
        data: parsedData,
      });
    };
    request.onerror = () => resolve(null);
  });
};

// 데이터 삭제
export const deleteData = async (
  db: IDBDatabase,
  table: string,
  id: string | number,
  validTables: string[],
): Promise<void> => {
  if (!validTables.includes(table)) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([table], "readwrite");
    const store = transaction.objectStore(table);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    store.delete(id);
  });
};
